import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {bool, func} from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {canDeletePublication} from '../auth.js';
import {
  useDeletePublication,
  usePermissions,
  usePublication,
  useUpdatePublication,
} from '../client.js';
import {cleanPublication} from '../utils.js';
import Page from './Page.jsx';
import PublicationForm from './form/PublicationForm.jsx';

function DeleteButton({onClick, disabled}) {
  return (
    <Tooltip title="delete publication">
      <IconButton
        disabled={disabled}
        onClick={onClick}
        color="error"
        aria-label="delete publication"
        component="span"
      >
        <DeleteOutlineOutlinedIcon />
      </IconButton>
    </Tooltip>
  );
}

DeleteButton.propTypes = {
  onClick: func.isRequired,
  disabled: bool,
};

function EditPublication() {
  const navigate = useNavigate();
  const [deleted, setDeleted] = useState(false);
  const {id} = useParams();
  const {data: permissions} = usePermissions();
  const [errorMessage, setErrorMessage] = useState('');
  const {data: publication} = usePublication(id);
  const {request, loading, error, data: update} = useUpdatePublication(id);
  const {
    request: deleteRequest,
    loading: deleteLoading,
    error: deleteError,
    data: deleteData,
  } = useDeletePublication(id);

  const onSubmit = data => {
    if (loading) {
      return;
    }

    request(cleanPublication(data));
  };

  const onDeleteClick = useCallback(async () => {
    if (deleteLoading) {
      return;
    }
    const confirmed = confirm(
      'Are you sure you want to delete this publication?',
    );
    if (confirmed) {
      await deleteRequest();
      setDeleted(true);
    }
  }, [deleteLoading, deleteRequest]);

  useEffect(() => {
    if (deleteLoading) {
      return;
    }
    if (deleteError) {
      setDeleted(false);
      setErrorMessage(deleteData?.message || deleteError.message);
      return;
    }
    if (deleted) {
      navigate(`/`);
    }
  }, [deleteData, deleteError, deleteLoading, deleted, navigate]);

  useEffect(() => {
    if (loading || !update) {
      return;
    }
    if (error) {
      setErrorMessage(update.message || error.message);
      return;
    }
    const url = `/publications/${update.id}`;
    navigate(url);
  }, [update, error, loading, navigate]);

  const links = [];
  if (canDeletePublication(permissions, publication)) {
    links.push(<DeleteButton key="delete" onClick={onDeleteClick} />);
  }

  return (
    <Page title="Edit Publication" links={links}>
      <Typography variant="h6" sx={{marginBottom: 2}}>
        Edit Publication
      </Typography>
      {errorMessage && (
        <Alert sx={{marginTop: 1, marginBottom: 1}} severity="error">
          {errorMessage}
        </Alert>
      )}

      {publication && (
        <PublicationForm publication={publication} onSubmit={onSubmit} />
      )}
    </Page>
  );
}

export default EditPublication;
